import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import {SortableHandle} from 'react-sortable-hoc';

import Icon from '../../components/Icon';
import Notification from '../../components/Notification';
import Menu from '../../components/Menu';
import Button from '../../components/Button';

import Input from '../../components/Input';
import DeletableCard from './DeletableCard';
import HeaderElementForm from './HeaderElementForm';
import DescriptionElementForm from './DescriptionElementForm';
import CalculationElementForm from './CalculationElementForm';
import AggregationElementForm from './AggregationElementForm';
import SubConfiguration from './SubConfiguration';
import JsonLogicBuilder from '../../components/JsonLogicBuilder';
import {getLogicBuilderVariables, WidthChangeContainer, SegmentedControl, SegmentOption, WidthWarning, WidthLabel} from './shared';
import { Checkbox } from './QuestionBuilder';
import { isValidCssColor } from '../../lib/color';

function ElementBuilder({form, catalog, element, onChange, onDelete, copyFormElement}) {
  const [optionsOpen, setOptionsOpen] = useState(false);

  const handleDelete = useCallback(() => {
    onDelete(element);
  }, [onDelete, element]);

  const [elementColour, setElementColour] = useState(
    getFormElementMetadata(element, 'elementColour')[0] || '#000000'
  );

  const [displayInline, setDisplayInline] = useState(
    getFormElementMetadata(element, 'displayInline')[0] || false
  )

  const [elementWidth, setElementWidth] = useState(
    getFormElementMetadata(element, 'elementWidth')[0] || '1'
  )

  const [isContextChecked, toggleContext] = useState(element.context);
  const [isConditionChecked, toggleIsConditionChecked] = useState(element.condition);
  const [condition, editCondition] = useState(element.condition);

  function setContext() {
    element.context = !isContextChecked;
    isContextChecked ? toggleContext(false) : toggleContext(true);
  }

  

  function setIsConditionChecked() {
    element.condition = null;
    toggleIsConditionChecked(!isConditionChecked);
  }

  function setEditCondition(conditionInput) {
    element.condition = conditionInput;
    editCondition(conditionInput);
  }

  function toggleElementOptions() {
   setOptionsOpen(!optionsOpen)
  }

  
  function handleElementColorChange(e) {
      const value = e; 
      const updatedMetadata = (element.formElementMeta && element.formElementMeta.filter((m) => m.key !== 'elementColour')) || [];

      if (value !== '#000000' && value !== elementColour) {
        setElementColour(value); //set the color to the value picked, and update if need be
        updatedMetadata.push({ key: 'elementColour', value: value });
      } //if not default or previous color, color swap the color and push the update 
      element.formElementMeta = updatedMetadata; //set the meta to the new updated metadata 
      onChange(element); //trigger the onChange to update the element in the form
  }

  function handleElementWidthChange(e) {
    const value = e.target.value;
    const updatedMetadata = (element.formElementMeta && element.formElementMeta.filter((m) => m.key !== 'elementWidth')) || [];

    setElementWidth(value);
    if(value !== '1' && value !== '1.0') {
      updatedMetadata.push({ key: 'elementWidth', value: value });
    }
    element.formElementMeta = updatedMetadata;
    onChange(element);
  }

  function handleDisplayInlineChange(e) {
    const value = e.target.checked;
    const updatedMetadata = (element.formElementMeta && element.formElementMeta.filter((m) => m.key !== 'displayInline')) || [];
    setDisplayInline(value);
    if(value === true){
      updatedMetadata.push({ key: 'displayInline', value: 'true' });
    }
    element.formElementMeta = updatedMetadata;
    onChange(element);
  }

  // clear display-inline when its order is set to 0
  useEffect(() => {
    if (element.order === 0 && displayInline) {
      const updatedMetadata = (element.formElementMeta && element.formElementMeta.filter((m) => m.key !== 'displayInline')) || [];
      element.formElementMeta = updatedMetadata;
      setDisplayInline(false);
      onChange(element);
    }
  }, [element.order]); 

  const ifNotFirstElement = element.order !== 0;

  return (
    <DeletableCard
      focusable
      disabled={false}
      dialogTitle={`Delete this ${element.type.toLowerCase()} element?`}
      dialogDescription="Are you sure you want to delete this element? This cannot be undone."
      ok="Delete"
      onOk={handleDelete}>
      <Header>
        <DragIcon>
          <Icon name="align-justify" size="1.5em" />
        </DragIcon>
        <FlexColumn>
        <h4 style={{ margin: 0 }}>{element.type.toLowerCase()}</h4>
        {element.type === 'DESCRIPTION' && (
          <p style = {{marginTop: '0.25rem', fontSize: '0.85rem', color: '#6c757d', marginBottom: 0 }}>
            Try using markdown to add headings, lists, links and emphasis.
          </p>
        )}
        </FlexColumn>

        <OptionsButtonWrapper>
        <ElementOptionsButton id={`element-options-${element.id}`} tertiary onClick={() => toggleElementOptions()}>
          <Icon name="chevron-down" />
        </ElementOptionsButton>
        </OptionsButtonWrapper>
        
        <Menu open={optionsOpen} relativeTo={`element-options-${element.id}`} onClose={() => toggleElementOptions()}>
          <Menu.Option onClick={() => copyFormElement(element)}>Copy Element</Menu.Option>
        </Menu>
      </Header>
      <Body>
        <ElementForm form={form} catalog={catalog} element={element} onChange={onChange} />
      <Checkbox>
        <input
          disabled={false}
          type="checkbox"
          checked={element.context}
          onChange={e => setContext()}
        />
        &nbsp;
        Show on setup
      </Checkbox>
      <Checkbox>
        <input
          disabled={false}
          type="checkbox"
          checked={!!isConditionChecked}
          onChange={e => setIsConditionChecked()}
        />
        &nbsp;
        Only show this element if...
      </Checkbox>
          {isConditionChecked && (
            <SubConfiguration>
              <SubConfiguration.Item>
                <JsonLogicBuilder
                  expand
                  variables={getLogicBuilderVariables(form, catalog)}
                  value={typeof condition === 'string' ? JSON.parse(condition) : null}
                  onChange={condition => setEditCondition(JSON.stringify(condition))}
                />
              </SubConfiguration.Item>
            </SubConfiguration>
          )}
        {element.type !== 'DESCRIPTION' && (
          <Input 
              id='backgroud-color-picker'
              label='Element Color:'
              labelPosition='start'
              color={elementColour || "#000000"}
              type='color'
              invalid={!isValidCssColor(elementColour || "#000000")}
              onChange={handleElementColorChange}
              />
        )}
        {ifNotFirstElement && (<Checkbox>
          <input
            disabled={false}
            type="checkbox"
            checked={displayInline}
            onChange={handleDisplayInlineChange}
          />
          &nbsp;
          Display inline with previous question/element
        </Checkbox>
        )}
        {element.type !== 'NUMERIC_GRID' && (
        <WidthChangeContainer>
          <div>
            <WidthLabel>Element width:</WidthLabel>
            <SegmentedControl>
              <SegmentOption
                  type="button"
                  active={elementWidth === '1' || elementWidth === '1.0'}
                  onClick={() => handleElementWidthChange({target: {value: '1'}})}
              >
                Full
              </SegmentOption>
              <SegmentOption
                  type="button"
                  active={elementWidth === '0.75'}
                  onClick={() => handleElementWidthChange({target: {value: '0.75'}})}
              >
                3/4
              </SegmentOption>
              <SegmentOption
                  type="button"
                  active={elementWidth === '0.67' || elementWidth === '0.66'}
                  onClick={() => handleElementWidthChange({target: {value: '0.67'}})}
              >
                2/3
              </SegmentOption>
              <SegmentOption
                  type="button"
                  active={elementWidth === '0.5'}
                  onClick={() => handleElementWidthChange({target: {value: '0.5'}})}
              >
                1/2
              </SegmentOption>
              <SegmentOption
                  type="button"
                  active={elementWidth === '0.33'}
                  onClick={() => handleElementWidthChange({target: {value: '0.33'}})}
              >
                1/3
              </SegmentOption>
              <SegmentOption
                  type="button"
                  active={elementWidth === '0.25'}
                  onClick={() => handleElementWidthChange({target: {value: '0.25'}})}
              >
                1/4
              </SegmentOption>
            </SegmentedControl>
          </div>
          {parseFloat(elementWidth) < 0.5 && (
              <WidthWarning>
                Elements narrower than 1/2 width will display at 1/2 width on phones
              </WidthWarning>
          )}
          {element.type === "DESCRIPTION" && (
              <WidthWarning>
                Description Elements with images will cause alignment
                and sizing issues when they have their width altered from FULL
              </WidthWarning>
          )}
        </WidthChangeContainer>
        )}
      </Body>
    </DeletableCard>
  );
}

function getFormElementMetadata(formElement, key) {
  if (formElement.formElementMeta && formElement.formElementMeta.length !==0)
  {
    return formElement.formElementMeta
    .filter(metadata => metadata.key === key)
    .map(metadata => metadata.value);
  } 
  else
  {
    return []; //returns empty list if elementMetaData does not exist 
  }
}

function ElementForm({ form, catalog, element, onChange }) {
  switch (element.type) {
    case 'HEADER':
      return <HeaderElementForm element={element} onChange={onChange} />;
    case 'DESCRIPTION':
      return <DescriptionElementForm element={element} onChange={onChange} />;
    case 'CALCULATION':
      return <CalculationElementForm form={form} catalog={catalog} element={element} onChange={onChange} />;
    case 'AGGREGATION':
      return <AggregationElementForm form={form} catalog={catalog} element={element} onChange={onChange} />;
    default:
      return <Notification error>Unknown element type: {element.type}</Notification>
  }
}

const Header = styled.div`
  display: flex;
  align-items: flex-start;

  h4 { 
    flex-grow: 1;
    margin: 0.4rem 0 0 0;
    padding: 0;
    font-size: 1.125rem;
    text-transform: capitalize;
  }
`;

const ElementOptionsButton = styled(Button)`
  margin-right: -1rem;
  margin-left: 1rem;
`;

const DragIcon = SortableHandle(styled.div`
  cursor: grab;
  color: #b4b4b4;
  margin-top: 0.4rem;
  margin-right: 1rem;
`);

const Body = styled.div`
  margin: 1rem 0 0 2.5rem;

  > * {
    margin-top: 0.625rem;
  }

  textarea {
    resize: vertical;
  }
`;

export const AboutMenu = styled(Menu)`
  max-width: 300px;
`;

const OptionsButtonWrapper = styled.div`
  align-self: flex-start;
  margin-top: 0.2rem;
`;

const FlexColumn = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;


export default ElementBuilder;